/**
 * Jun-2026 (User-Wunsch) — Funktions-Erklär-Dreieck für Dispo-Modals.
 *
 * Kleines farbiges Dreieck in der oberen linken Ecke eines Modals. Klick öffnet
 * ein Popup, das ALLE Funktionen hinter diesem Bereich erklärt.
 *
 * Gating:
 *   - System LIVE (APP_ENV=production)  → nur Admins sehen/klicken das Dreieck.
 *   - Solange NICHT live (preview/dev)  → für jeden sichtbar/klickbar.
 */
import { useEffect, useState } from "react";
import { Info, X } from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { DISPO_GUIDES } from "../../../lib/dispoGuides";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";

let _flagsCache = null; // einmal je Session laden

export function FeatureGuideTriangle({ guideKey, title, features = [] }) {
    // Zentrale Texte aus dispoGuides.js (per guideKey) — mit Fallback auf Props.
    const guide = guideKey ? DISPO_GUIDES[guideKey] : null;
    const resolvedTitle = (guide && guide.title) || title;
    const resolvedFeatures = (guide && guide.features) || features;
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [flags, setFlags] = useState(_flagsCache);

    useEffect(() => {
        if (_flagsCache) { setFlags(_flagsCache); return; }
        let alive = true;
        api.get("/system/flags")
            .then((r) => { _flagsCache = r.data; if (alive) setFlags(r.data); })
            .catch(() => { _flagsCache = { is_live: false }; if (alive) setFlags({ is_live: false }); });
        return () => { alive = false; };
    }, []);

    const isLive = !!flags?.is_live;
    const isAdmin = (user && user.role === "admin");
    const allowed = !isLive || isAdmin; // live → nur Admin; sonst jeder
    if (!allowed) return null;

    return (
        <>
            {/* Farbiges Eck-Dreieck oben links */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                title="Funktionen dieses Bereichs anzeigen"
                aria-label="Funktionen dieses Bereichs anzeigen"
                data-testid="feature-guide-triangle"
                className="absolute top-0 left-0 z-30 group"
                style={{ width: 0, height: 0, borderTop: "30px solid #FFCC00", borderRight: "30px solid transparent" }}
            >
                <Info className="w-3 h-3 text-[#111827] absolute -top-[26px] left-[3px] pointer-events-none" />
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="feature-guide-popup">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Info className="w-5 h-5 text-[#FFCC00]" />
                            {resolvedTitle || "Funktionen dieses Bereichs"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-1.5 mb-1" data-testid="feature-guide-mode">
                        {isLive
                            ? "Live-Modus — diese Erklärung ist nur für Administratoren sichtbar."
                            : "Vorschau-Modus — diese Erklärung ist aktuell für alle sichtbar. Im Live-Betrieb nur für Administratoren."}
                    </div>
                    <ul className="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
                        {resolvedFeatures.map((f, i) => (
                            <li key={i} className="flex gap-2.5" data-testid={`feature-guide-item-${i}`}>
                                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#FFCC00] shrink-0" />
                                <div>
                                    <div className="text-sm font-bold text-gray-900">{f.name}</div>
                                    <div className="text-[12px] text-gray-600 leading-snug">{f.desc}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default FeatureGuideTriangle;
